import * as React from 'react';
import Hitarea from '@packages/systems/design-system/Hitarea';
import PseudoProvider from '@packages/systems/design-system/PseudoProvider';

import {
  oneLineText,
  unselectable,
} from '@packages/systems/design-system/constants/Styles';
import {weakMemo} from '@packages/utilities/memo';
import {
  useTheme,
  ThemeContext,
  styled,
  tokens,
} from '@packages/utilities/styled';
import type {PropertyOrigin} from '@packages/systems/style-legacy/values';
import {DefaultOrigin} from '@packages/systems/style-legacy/values';
import type {Theme} from '@packages/utilities/styled';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {
  DeprecatedPopoverBridge,
  type NamedPopoverProps,
  type RenderPopoverContentFnType,
} from '@packages/systems/design-system/PopoverBridge';
import ButtonShell from '@packages/systems/design-system/ButtonShell';
import {mergeRefs} from '@packages/systems/spring';
import type {CSSProperties} from '@packages/utilities/css-properties-type';
import type {DesignerTranslationFn} from '@packages/systems/designer-translations';

// Adjustments to prevent the reset button version of the label from making
//   the label and the rest of the component (to the right and below) visually
//   "jump" when the reset becomes available and when it disappears.
export const PropertyLabelResetButtonLayoutFix = styled('div')(({applyFix}) =>
  applyFix
    ? {
        margin: '-2px -4px',
      }
    : {}
);

// Adjustments to prevent the reset button version of the inline label from making
//   the label and the rest of the component (to the right and below) visually
//   "jump" when the reset becomes available and when it disappears.
export const PropertyLabelInlineResetButtonLayoutFix = styled('div')(
  ({applyFix}) => ({
    display: 'inline',
    ...(applyFix
      ? {
          margin: '0 -4px',
        }
      : {}),
  })
);

type StylePanelPopover = (props: NamedPopoverProps) => React.ReactElement;

/**
 * How much the highlight color "bleeds" outside the actual
 * content box of the PropertyLabel element
 */
const BLEED_WIDTH = 2;

// @ts-expect-error - TS7031 - Binding element 'theme' implicitly has an 'any' type. | TS7031 - Binding element 'color' implicitly has an 'any' type.
const Strikethru = ({theme, color}) => (
  <div
    style={{
      backgroundColor: color,
      height: 1,
      position: 'absolute',
      top: '50%',
      width: `calc(100% + ${BLEED_WIDTH * 2}px)`,
      left: -BLEED_WIDTH,
      boxShadow: theme.shadows.small,
      overflowX: 'hidden',
    }}
  />
);

// @ts-expect-error - TS7006 - Parameter 'theme' implicitly has an 'any' type.
const getForegroundColors = weakMemo((theme) => ({
  local: theme.colors.properties.local.foreground,
  inherited: theme.colors.properties.inherited.foreground,
  default: theme.colors.properties.default.foreground,
  overridden: theme.colors.properties.overridden.foreground,
  id: theme.colors.properties.id.foreground,
}));

// @ts-expect-error - TS7006 - Parameter 'theme' implicitly has an 'any' type.
const getBackgroundColors = weakMemo((theme) => ({
  local: theme.colors.properties.local.background,
  inherited: theme.colors.properties.inherited.background,
  default: theme.colors.properties.default.background,
  overridden: theme.colors.properties.overridden.background,
  id: theme.colors.properties.id.background,
}));

// @ts-expect-error - TS7006 - Parameter 'theme' implicitly has an 'any' type.
const getBackgroundHoverColors = weakMemo((theme) => ({
  local: theme.colors.properties.local.backgroundHover,
  inherited: theme.colors.properties.inherited.backgroundHover,
  default: theme.colors.properties.default.backgroundHover,
  overridden: theme.colors.properties.overridden.backgroundHover,
  id: theme.colors.properties.id.backgroundHover,
}));

type PropertyLabelSharedProps = {
  disabled?: boolean;
  hover?: boolean;
  small?: boolean;
  narrow?: boolean;
  centered?: boolean;
  children: React.ReactNode;
  style?: CSSProperties;
  tooltip?: React.ReactNode;
  name?: string;
  id?: string;
};

const defaultOrigin = DefaultOrigin();
export type PropertyLabelBaseProps = PropertyLabelSharedProps & {
  origin?: PropertyOrigin;
};

export const PropertyLabelBase = React.forwardRef<
  HTMLDivElement,
  PropertyLabelBaseProps
>(
  (
    {
      origin = defaultOrigin,
      hover,
      small,
      narrow,
      centered,
      children,
      style: _style,
      disabled,
      tooltip,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();

    const hasWarning = origin.type === 'local' ? origin.hasWarning : false;
    const foregroundColor = hasWarning
      ? tokens.colors.yellowText
      : getForegroundColors(theme)[origin.type];
    const strikethruColor =
      origin.type === 'overridden' ? tokens.colors.redText : undefined;
    const backgroundColor =
      origin.type === 'default' && !hasWarning
        ? 'transparent'
        : hasWarning
          ? hover
            ? tokens.colors.yellowTransparentHover
            : tokens.colors.yellowTransparent
          : hover
            ? getBackgroundHoverColors(theme)[origin.type]
            : getBackgroundColors(theme)[origin.type];

    const typography: {
      fontSize: number;
      fontFamily: string;
      fontWeight: number;
      lineHeight: string;
      letterSpacing: string;
    } = narrow
      ? theme.typography.labelNarrow
      : small
        ? theme.typography.labelSmall
        : theme.typography.label;

    const style = {
      ...unselectable,
      ...oneLineText,
      ...typography,
      lineHeight: 'normal',
      display: 'inline-flex',
      cursor: 'default',
      color: foregroundColor,
      background: backgroundColor,
      padding: BLEED_WIDTH,
      marginLeft: -BLEED_WIDTH,
      borderRadius: 2,
      maxWidth: '100%',
      boxSizing: 'content-box', // as we do not want padding values to be included
      justifySelf: centered ? 'center' : 'start',
      // Needed for Strikethru positioning:
      position: 'relative',
      opacity: disabled ? 0.3 : 1,
      ..._style,
    } as const;

    const {
      /* eslint-disable unused-imports/no-unused-vars */
      // The following four fields are still present on `props`, but are invalid
      // HTML attributes. This causes runtime console.error messages. To address
      // this we're removing them from the rest of the props before spreading as
      // attributes onto the div.

      active,

      focus,

      keyboardFocus,

      t,
      /* eslint-enable unused-imports/no-unused-vars */
      ...attributes
    } = props as {
      active?: boolean;
      focus?: boolean;
      keyboardFocus?: boolean;
      t?: DesignerTranslationFn;
      // These two known attributes remain. There may be others at runtime which
      // aren't documented or annotated on types:
      name?: string;
      id?: string;
    };

    const renderLabel = (
      <div
        {...attributes}
        style={style}
        data-legacy-ds="PropertyLabel"
        // Used to check the state of the label in Cypress.
        data-origin={origin.type}
        data-resettable={Boolean(origin.type !== 'default')}
        ref={ref}
      >
        {children}
        {strikethruColor && (
          <Strikethru theme={theme} color={strikethruColor} />
        )}
      </div>
    );

    if (tooltip) {
      return (
        <TooltipTrigger alternate content={tooltip}>
          {renderLabel}
        </TooltipTrigger>
      );
    }
    return renderLabel;
  }
);

PropertyLabelBase.displayName = 'PropertyLabelBase';

type PropertyLabelButtonProps = {
  origin: PropertyOrigin;
  // Accept an optional arg so popover-trigger callbacks (e.g.
  // `openPopover: (arg: unknown) => void` from `Popover`) are assignable.
  // The runtime always invokes these handlers with no arguments.
  onResetValueClick: (arg?: unknown) => void;
  onClick: (arg?: unknown) => void;
  tooltip?: React.ReactNode;
  label?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  selected?: boolean;
  style?: CSSProperties;
  hover?: boolean;
  active?: boolean;
  brightness?: 'normal' | 'dim';
  ['aria-label']?: string;
  ['aria-controls']?: string;
  ['aria-expanded']?: boolean;
  ['aria-haspopup']?: string;
  id?: string;
  tabIndex?: string | number;
  type?: string;
  ['data-automation-id']?: string;
  _ref?: React.RefObject<HTMLDivElement>;
};

type StatefulPropertyLabelProps = {
  active?: boolean;
  fallback: string;
  hover?: boolean;
  origin: PropertyOrigin;
  selected?: boolean;
};

const getStatefulBackgroundColor = ({
  selected,
  hover,
  origin,
  fallback,
}: StatefulPropertyLabelProps) => {
  let backgroundColor = fallback;

  /**
   * default origin types render the grays.
   * We only want to style the default origin types.
   */
  if (origin.type !== 'default') {
    return backgroundColor;
  }

  if (selected) {
    backgroundColor = tokens.colors.background3;
  } else {
    if (hover) {
      backgroundColor = tokens.colors.background2;
    }
  }

  return backgroundColor;
};

const getStatefulTextColor = ({
  selected,
  hover,
  origin,
  active,
  fallback,
}: StatefulPropertyLabelProps) => {
  let color = fallback;

  /**
   * default origin types render the grays.
   * We only want to style the default origin types.
   */
  if (origin.type !== 'default') {
    return color;
  }

  if (selected || active || hover) {
    color = tokens.colors.textPrimary;
  }

  return color;
};

export class PropertyLabelButton extends React.Component<PropertyLabelButtonProps> {
  static contextType = ThemeContext;

  handleClick = (e: React.MouseEvent<EventTarget>) => {
    if (e.altKey) {
      this.props.onResetValueClick();
    } else {
      this.props.onClick();
    }
  };

  render() {
    const {
      tooltip,
      selected,
      icon,
      style: _style,
      label,
      origin,
      hover,
      active,
      brightness,
      _ref,
      ...rest
    } = this.props;
    // @ts-expect-error [PDTY-2706] - TS2322 - Type 'unknown' is not assignable to type 'Theme'.
    const theme: Theme = this.context;
    const isOriginDefault = origin.type === 'default';

    const foregroundColor = getForegroundColors(theme)[origin.type];

    const backgroundColor = !isOriginDefault
      ? hover
        ? getBackgroundHoverColors(theme)[origin.type]
        : getBackgroundColors(theme)[origin.type]
      : hover
        ? tokens.colors.background2
        : 'transparent';

    const color =
      brightness === 'dim' && origin.type === 'default' && !hover
        ? tokens.colors.textSecondary
        : foregroundColor;

    return (
      <PseudoProvider>
        {({hover: pseudoHover}) => {
          /**
           * Walkthrough of how the colors are determined:
           * https://www.loom.com/share/d9baa1f0180f4e8abcb119b7d15fd278
           */
          const isHover = hover || pseudoHover;
          const finalBackgroundColor = getStatefulBackgroundColor({
            selected,
            active,
            hover: isHover,
            origin,
            fallback: backgroundColor,
          });
          const finalColor = getStatefulTextColor({
            selected,
            active,
            hover: isHover,
            origin,
            fallback: color,
          });

          return (
            <PropertyLabelBase
              {...rest}
              origin={origin}
              // @ts-expect-error - TS2322 - Type '{ children: {} | null; origin: PropertyOrigin; theme: Theme; hover: boolean | undefined; active: boolean | undefined; tooltip: ReactNode; label: string | undefined; ... 5 more ...; "data-automation-id"?: string | undefined; }' is not assignable to type 'IntrinsicAttributes & PropertyLabelSharedProps & { origin?: PropertyOrigin | undefined; }'.
              theme={theme}
              hover={isHover}
              active={active}
              tooltip={tooltip}
              label={label}
              style={{
                color: finalColor,
                background: finalBackgroundColor,
                boxShadow: `0px 0px 0px ${
                  brightness === 'dim' ? 1 : BLEED_WIDTH
                }px ${finalBackgroundColor}`,
                ..._style,
              }}
              onClick={this.handleClick}
              centered
              ref={_ref}
            >
              {icon || null}
            </PropertyLabelBase>
          );
        }}
      </PseudoProvider>
    );
  }
}

// @ts-expect-error - TS7006 - Parameter 'openPopover' implicitly has an 'any' type.
const onClick = weakMemo((openPopover) =>
  // @ts-expect-error - TS7006 - Parameter 'onResetValueClick' implicitly has an 'any' type.
  weakMemo((onResetValueClick) => (e: React.MouseEvent<EventTarget>) => {
    if (e.altKey) {
      onResetValueClick();
    } else {
      openPopover();
    }
  })
);
/**
 * A label for a property of some sort. 🤷‍
 *
 * A typical example would be a style attribute (e.g. `border-color`) in
 * the Style Panel.
 *
 * **[📖 View in Storybook ➡️](http://localhost:9001/?selectedKind=Property%20Label&selectedStory=Default)**
 */
export type PropertyLabelProps = PropertyLabelSharedProps & {
  origin: PropertyOrigin;
  onResetValueClick: () => void;
  /** For labels with no reset to run: alt-click shows the popover instead. */
  altClickOpensPopover?: boolean;
  onPopoverChange?: (arg1: {open: boolean}) => void;
  renderMenuContent?: (closePopover: () => void) => () => React.ReactElement;
  Popover?: typeof DeprecatedPopoverBridge | StylePanelPopover;
  renderPopoverContent: RenderPopoverContentFnType;
  popoverProps?: NamedPopoverProps;
  as?: 'button';
  buttonStyle?: CSSProperties;
  showMask?: NamedPopoverProps['showMask'];
  placement?: NamedPopoverProps['placement'];
};
const PropertyLabel = React.forwardRef<HTMLDivElement, PropertyLabelProps>(
  (props, ref) => {
    const {
      onResetValueClick,
      altClickOpensPopover,
      renderPopoverContent,
      Popover: PopoverProp,
      as,
      showMask,
      placement,
      ...rest
    } = props;

    if (
      (props.origin.type === 'default' && !props.renderMenuContent) ||
      props.disabled
    ) {
      return <PropertyLabelBase ref={ref} {...rest} />;
    }

    const ActivePopover = PopoverProp || DeprecatedPopoverBridge;

    return (
      <ActivePopover
        showArrow={false}
        renderPopoverContent={renderPopoverContent}
        showMask={showMask}
        placement={placement}
        onChange={props.onPopoverChange}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        name={props.name}
        {...(props.popoverProps || {})}
      >
        {({Anchor, openPopover}) => {
          if (as === 'button') {
            return (
              <Anchor asChild={false}>
                {(anchorRef) => (
                  <PseudoProvider>
                    {({hover, ...restPseudo}) => (
                      <ButtonShell
                        {...restPseudo}
                        structure="ghost"
                        hover={false}
                        style={rest.buttonStyle}
                        onClick={onClick(openPopover)(
                          altClickOpensPopover ? openPopover : onResetValueClick
                        )}
                        _ref={mergeRefs([anchorRef, ref])}
                      >
                        <PropertyLabelBase hover={hover} {...rest} />
                      </ButtonShell>
                    )}
                  </PseudoProvider>
                )}
              </Anchor>
            );
          }

          return (
            <Anchor asChild={false}>
              {(anchorRef) => (
                <Hitarea>
                  <PropertyLabelBase
                    {...rest}
                    // @ts-expect-error - TS2322 - Type '{ onClick: (e: MouseEvent<EventTarget, MouseEvent>) => void; disabled?: boolean | undefined; hover?: boolean | undefined; small?: boolean | undefined; ... 11 more ...; buttonStyle?: ObjMap<...> | undefined; }' is not assignable to type 'IntrinsicAttributes & PropertyLabelSharedProps & { origin?: PropertyOrigin | undefined; }'.
                    onClick={onClick(openPopover)(
                      altClickOpensPopover ? openPopover : onResetValueClick
                    )}
                    ref={mergeRefs([
                      anchorRef as React.RefObject<HTMLDivElement>,
                      ref,
                    ])}
                  />
                </Hitarea>
              )}
            </Anchor>
          );
        }}
      </ActivePopover>
    );
  }
);

export default PropertyLabel;
